"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const galleryItems = [{
  id: "item-1",
  category: "treatment",
  title: "Sacramento Wood Floor Treatment",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_wood_floor_treatment.png"
}, {
  id: "item-2",
  category: "assembly",
  title: "Sacramento Outdoor Furniture Assembly",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_outdoor_furniture_assembly.png"
}, {
  id: "item-3",
  category: "removal",
  title: "Sacramento Floor & Carpet Removal",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_floor_carpet_removal.png"
}, {
  id: "item-4",
  category: "cleaning",
  title: "Sacramento Deep Carpet Cleaning",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_deep_carpet_cleaning.png"
}, {
  id: "item-5",
  category: "assembly",
  title: "Sacramento Expert Furniture Assembly",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_expert_furniture_assembly.png"
}, {
  id: "item-6",
  category: "assembly",
  title: "Sacramento Outdoor Play Set Assembly",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_outdoor_playset_assembly.png"
}, {
  id: "item-7",
  category: "removal",
  title: "Sacramento Professional Debris Removal",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_debris_removal_service.png"
}, {
  id: "item-8",
  category: "assembly",
  title: "Sacramento Home Office Assembly",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_office_assembly.png"
}, {
  id: "item-9",
  category: "treatment",
  title: "Sacramento Hardwood Surface Refinement",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/sacramento_hardwood_surface_treatment.png"
}];
const categories = [{
  value: "all",
  label: "All Projects"
}, {
  value: "assembly",
  label: "Assembly"
}, {
  value: "removal",
  label: "Removal"
}, {
  value: "cleaning",
  label: "Cleaning"
}, {
  value: "treatment",
  label: "Treatment"
}];
export default function TheVisualJournal() {
  const [activeTab, setActiveTab] = useState("all");
  const [displayTab, setDisplayTab] = useState("all");
  const [isFlipping, setIsFlipping] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const handleTabChange = (newTab: string) => {
    if (newTab === activeTab) return;
    setIsFlipping(true);
    setActiveTab(newTab);
    setVisibleCount(6);
    setTimeout(() => {
      setDisplayTab(newTab);
      setIsFlipping(false);
    }, 400);
  };
  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + 3, galleryItems.length));
  };
  const filteredItems = useMemo(() => {
    const filtered = displayTab === "all" ? galleryItems : galleryItems.filter(item => item.category === displayTab);
    return filtered.slice(0, visibleCount);
  }, [displayTab, visibleCount]);
  const totalFilteredCount = useMemo(() => {
    return displayTab === "all" ? galleryItems.length : galleryItems.filter(item => item.category === displayTab).length;
  }, [displayTab]);
  return <section data-section-id="151410" id="sacramento-project-gallery" className="bg-background py-24 md:py-32 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-foreground tracking-tighter font-extrabold">
            Sacramento Assembly, Removal & Cleaning
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg md:text-xl font-sans font-medium">
            Professional furniture setup, expert floor removal, and specialized deep cleaning throughout Sacramento. We deliver fast, reliable results for every home renewal project.
          </p>
        </div>

        {}
        <div className="flex justify-center mb-12">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full max-w-2xl">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 bg-muted p-1 rounded-md border border-border h-auto md:h-10">
              {categories.map((category, i) => <TabsTrigger key={category.value} value={category.value} data-index={i} className="rounded-sm text-sm md:text-base font-sans font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md py-2 md:py-1">
                  {category.label}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        {}
        <div className="relative min-h-[600px]" style={{
        perspective: "1200px"
      }}>
          <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 transition-all duration-400 ease-in-out transform-gpu", isFlipping ? "opacity-0 [transform:rotateY(90deg)_scale(0.95)]" : "opacity-100 [transform:rotateY(0deg)_scale(1)]")}>
            {filteredItems.map((item, i) => <Card key={item.id} data-index={i} className="group overflow-hidden border border-border rounded-lg bg-card shadow-sm hover:shadow-xl transition-shadow duration-500">
                <CardContent className="p-0 relative aspect-[4/5] overflow-hidden rounded-lg">
                  {}
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                  
                  {}
                  <div className="absolute inset-0 opacity-[0.1] mix-blend-overlay pointer-events-none" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
              }} />

                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                    <span className="text-primary-foreground/80 text-xs uppercase tracking-widest mb-2 font-mono font-bold">
                      {item.category}
                    </span>
                    <h3 className="text-primary-foreground text-xl font-default font-extrabold tracking-tight">
                      {item.title}
                    </h3>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {}
        {visibleCount < totalFilteredCount && <div className="mt-16 flex justify-center">
            <Button variant="link" size="lg" onClick={handleLoadMore} className="text-primary hover:text-primary/80 font-sans font-bold tracking-wider uppercase text-sm group">
              View More Projects
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>}

        {}
        <div className="mt-20 flex flex-col items-center justify-center space-y-8">
          <div className="h-px w-full max-w-md bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="text-center space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight">Ready to Renew Your Home?</h3>
            <p className="text-muted-foreground font-medium">Call or WhatsApp us today for a free quote in Sacramento.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-2xl px-4">
            <Button asChild size="lg" className="w-full sm:flex-1 h-14 text-lg font-bold shadow-lg">
              <Link to="tel:16105498900">Call Now: 1610-549-8900</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:flex-1 h-14 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/5">
              <Link to="https://wa.me/16105498900">
                WhatsApp Us
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}